import { fetchData } from 'services/api/fetchData';
import { HeaderButtons, HeaderButtonsConfig } from 'types/ButtonTypes';
import { DetailCardType } from 'types/DetailPageTypes';
import { ApiRequestType } from 'types/FieldTypes';
import { QueryType } from 'types/ListTypes';

/*
 * fetchFieldData.tsx
 * General utility function that fetches the data of fields and
 * fetches dropdown data. These function can be imported in detailPages
 * etc.
 */

// Fetches the data of the item id of the detail page to show in the fields
export const fetchFieldData = async (
    apiObject: string, 
    itemId: string,
    handleLogout: () => Promise<void>
) => {
    return fetchData({ apiObject, itemId, handleLogout });
};

// Module level map which holds the in flight dropdown requests to deduplicate concurrent identical fetches
const inflightRequests = new Map<string, Promise<any>>();

// Describes a single deduplicated dropdown request with the dropdown data keys to assign the response to
interface DropdownRequest {
    apiUrl?: string;
    apiObject?: string;
    cacheKey: string;
    params: any;
    useCache: boolean;
    assignments: { object: string; useResults: boolean }[];
}

// Loops over the detailcards to collect the dropdown fields to request. It deduplicates requests with the
// same endpoint and params and fetches all dropdown data in parallel instead of one request at a time
export const fetchDropdownData = async (
    sections: DetailCardType[],
    dropdownData: any,
    activeFeatures: string[] = [],
    getCache: (url: string, params?: any) => any,
    setCache: (url: string, data: any, params?: any) => void,
    handleLogout: () => Promise<void>,
    headerButtons?: HeaderButtonsConfig,
) => {
    const newDropdownData = { ...dropdownData };

    // Initialize the map with the deduplicated requests to fetch
    const requestMap = new Map<string, DropdownRequest>();

    // Generate a unique request key based on the url and the sorted params
    const generateRequestKey = (url: string, params: any): string => {
        if (!params || Object.keys(params).length === 0) return url;
        return `${url}?${JSON.stringify(params, Object.keys(params).sort())}`;
    };

    // Add a request to the request map and merge requests with the same url and params into one request
    const addRequest = (
        { apiUrl, apiObject, params, object, useCache, useResults }:
        { apiUrl?: string; apiObject?: string; params: any; object: string; useCache: boolean; useResults: boolean }
    ) => {
        // Determine the cache key of the request based on the url convention of the fetch data function
        const cacheKey = apiUrl ?? `get_${apiObject}_list`;

        // Get the existing request with the same url and params from the map
        const requestKey = generateRequestKey(cacheKey, params);
        const existingRequest = requestMap.get(requestKey);

        // Add the dropdown data key to the existing request in case of a duplicated request
        if (existingRequest) {
            existingRequest.assignments.push({ object, useResults });
            existingRequest.useCache = existingRequest.useCache || useCache;
            return;
        }

        // Otherwise add the request to the map
        requestMap.set(requestKey, { apiUrl, apiObject, cacheKey, params, useCache, assignments: [{ object, useResults }] });
    };

    // Helper function to collect the requests of fields with apiObject or apiRequest props
    const collectFromFields = (fields: any[]) => {
        for (const field of fields ?? []) {

            // Skip fetching if the feature of the field is not active
            if (field?.activeFeature) {
                if (!activeFeatures.includes(field.activeFeature)) continue;
            }

            if (field.apiRequest) {
                // First handle the apiRequest props. Fields with this prop can also have a apiObject prop
                for (const request of field.apiRequest as ApiRequestType[]) {
                    const { endpoint, params, object, useCache = false } = request;
                    addRequest({ apiUrl: endpoint, params, object, useCache, useResults: false });
                }
            } else if (field.apiObject) {
                addRequest({ apiObject: field.apiObject, params: {}, object: field.apiObject, useCache: true, useResults: false });
            }

            // Loop through the fields in field columns, call the function recursively
            if (field.fieldColumns) {
                for (const column of field.fieldColumns) {
                    collectFromFields(column.fields);
                }
            }
        }
    };

    // Loop through the items of the given section. A section may contain detailCards with fields inside them, or may contain the fields directly
    for (const item of sections) {
        // If the item in the section contains a collection of fields (for example sections or detailCards)
        if (item.fields) {
            collectFromFields(item.fields);
        // If the item in the configSet contains fieldColumns with a collection of fields itself
        } else if (item.fieldColumns) {
            for (const column of item.fieldColumns) {
                collectFromFields(column.fields);
            }
        // Otherwise treat the item as a single field, so flat field lists keep working and card items without fields are skipped
        } else {
            collectFromFields([item]);
        }
    }

    // If header buttons are given, loop through the header button config to find query props
    if (headerButtons) {
        // Initialize array to store the buttons to process
        let buttonsToProcess: HeaderButtons = [];

        // If the given header buttons config is one array, use the header buttons config directly
        if (Array.isArray(headerButtons)) {
            buttonsToProcess = headerButtons;
        }

        // When the given header buttons config exist of left- and right buttons, loop through these configs
        else {
            const { leftButtons = [], rightButtons = [] } = headerButtons;
            buttonsToProcess = [...leftButtons, ...rightButtons];
        }

        // Collect the requests of the header button queries
        for (const button of buttonsToProcess) {
            if ('query' in button && button.query) {
                const { endpoint, defaultParams, object, useCache = false } = button.query as QueryType;
                addRequest({ apiUrl: endpoint, params: defaultParams, object, useCache, useResults: true });
            }
        }
    }

    // Fetch a single request, trying the cache first when the request allows it
    const fetchRequest = async (request: DropdownRequest) => {
        // Get the data from the cache
        if (request.useCache) {
            const cached = getCache(request.cacheKey, request.params);
            if (cached) return cached;
        }

        // Await the in flight request if the same request is already running
        const requestKey = generateRequestKey(request.cacheKey, request.params);
        const inflightRequest = inflightRequests.get(requestKey);
        if (inflightRequest) return inflightRequest;

        // Otherwise fetch the data and register the request as in flight
        const requestPromise = (async () => {
            const freshData = request.apiUrl
                ? await fetchData({ apiUrl: request.apiUrl, params: request.params, handleLogout })
                : await fetchData({ apiObject: request.apiObject, params: request.params, handleLogout });

            // Store the data in the cache
            if (request.useCache) {
                setCache(request.cacheKey, freshData, request.params);
            }

            return freshData;
        })().finally(() => inflightRequests.delete(requestKey));
        inflightRequests.set(requestKey, requestPromise);

        return requestPromise;
    };

    // Fetch all collected requests in parallel and assign the responses to the dropdown data keys
    await Promise.all(Array.from(requestMap.values()).map(async (request) => {
        const data = await fetchRequest(request);
        for (const assignment of request.assignments) {
            newDropdownData[assignment.object] = assignment.useResults ? data.results : data;
        }
    }));

    return newDropdownData;
};

// Loops over the detailcards to find a field of the multiselect type which has the group by field prop set.
// This means the options in the multiselect dropdown are grouped by a field. This function fetches the data
// of these groups so that we can show a name or a label instead of just a group id.
export const fetchGroupOptions = async (
    detailCards: any, 
    groupOptions: any,
    activeFeatures: string[] = [],
    getCache: (url: string, params?: any) => any,
    setCache: (url: string, data: any, params?: any) => void,
    handleLogout: () => Promise<void>
) => {
    const newGroupOptions = { ...groupOptions };

    // First, try to get the data from the cache, otherwise fetch it again
    const fetchDataWithCache = async (apiUrl: string, params: any, useCache: boolean) => {
        if (useCache) {
            // Get the data from the cache
            const cached = getCache(apiUrl, params);
            if (cached) return cached;

            // If the data doesn't exist, fetch it
            const freshData = await fetchData({ apiUrl, params, handleLogout });
            setCache(apiUrl, freshData, params);
            return freshData;
        } else {
            return await fetchData({ apiUrl, params, handleLogout });
        }
    };

    // Loop through the cards and its fields to check for an api object to fetch
    for (const card of detailCards) {
        for (const field of card.fields ?? []) {

            // Skip fetching if the feature of the field is not active
            if (field?.activeFeature) {
                if (!activeFeatures.includes(field.activeFeature)) continue;
            }

            // Fetch the multiselect group data
            if (field.type === 'multiselect' && field.groupByField) {
                // const results = await fetchData({ apiObject: field.groupByField?.apiObject, params: { is_active: 'true' }, handleLogout });

                const results = await fetchDataWithCache(field.groupByField.apiObject, { is_active: 'true' }, field.groupByField.useCache)
                const fieldName = field.groupByField.apiField;
                
                // Only extract the id and the given field name from the data
                const extractedFields = results.results.map((item: any) => ({
                    id: item.id,
                    [fieldName]: item[fieldName],
                }));
                newGroupOptions[field.groupByField?.apiObject] = extractedFields
            }
        }
    }
    return newGroupOptions;
};

// Fetch the field data and the dropdown options for all fields in all detailcards of the page in once. This
// is done to update the loadingstate once.
export const fetchDataAndOptionsOfDetailCards = async (
    apiObject: string, 
    itemId: string, 
    dropdownData: any, 
    groupOptions: any,
    headerButtons: HeaderButtonsConfig | undefined, 
    sections: any, 
    activeFeatures: string[] = [],
    setData: any, 
    setDropdownData: any, 
    setGroupOptions: any, 
    setLoadStatus: any,
    getCache: (url: string, params?: any) => any,
    setCache: (url: string, data: any, params?: any) => void,
    handleLogout: () => Promise<void>,
    setHeader?: any,
    headerTitleDataField?: string,
) => {
    try {
        const [fetchedFieldData, fetchedDropdownData, fetchedGroupOptions] = await Promise.all([
            fetchFieldData(apiObject, itemId, handleLogout),
            fetchDropdownData(sections, dropdownData, activeFeatures, getCache, setCache, handleLogout, headerButtons),
            fetchGroupOptions(sections, groupOptions, activeFeatures, getCache, setCache, handleLogout)
        ]);
        setData(fetchedFieldData);
        setDropdownData(fetchedDropdownData);
        setGroupOptions(fetchedGroupOptions);
        if (headerTitleDataField) {
            setHeader(fetchedFieldData[headerTitleDataField])
        }
        setLoadStatus("loaded");
        return [fetchedFieldData, fetchedDropdownData, fetchedGroupOptions];
    } catch (error) {
        console.error(error);
    }
};

// Fetch the dropdown data only of the given fields
export const fetchDropdownDataFromFields = async (
    apiObject: string, 
    params: any = {},
    handleLogout: () => Promise<void>
) => {
    try {
        const data = await fetchData({ apiObject, params, handleLogout });
        return data;
    } catch (error) {
        console.log(`Failed to fetch data for ${apiObject}`, error);
    }
};